/**
 * Form Engine Portal navigation with shared auth token.
 * When opening FormEnginePortal from Healthcare Portal, we pass the same access token
 * so FormEnginePortal can call APIs (Credentialing, Data Access, Form Data Service, etc.) without
 * "invalid_token" / "The signature is invalid".
 *
 * FormEnginePortal expects the token in the URL (see FormEnginePortal frontend/src/auth/sharedAuth.ts):
 * - Fragment: #access_token=<token>
 * - Query: ?access_token=<token>
 * This param name MUST match FormEnginePortal's SHARED_AUTH_ACCESS_TOKEN_PARAM.
 *
 * When FormEnginePortal is opened in a new tab, it requests localStorage/sessionStorage via postMessage.
 * This module registers a listener that responds with all items (same keys) so FormEnginePortal has
 * the same storage (e.g. MSAL cache keys, app state) as Healthcare Portal.
 */
const SHARED_AUTH_ACCESS_TOKEN_PARAM = 'access_token';

/** postMessage type: FormEnginePortal requests storage from opener. Must match FormEnginePortal sharedAuth. */
const STORAGE_TRANSFER_REQUEST = 'HEALTHCARE_PORTAL_REQUEST_STORAGE';
/** postMessage type: We send storage to FormEnginePortal. Must match FormEnginePortal sharedAuth. */
const STORAGE_TRANSFER_RESPONSE = 'HEALTHCARE_PORTAL_STORAGE_TRANSFER';

import { FORM_ENGINE_PORTAL_URL } from '../config/api.config';
import { FORM_ENGINE_PORTAL_WINDOW_NAME } from '../constants/formEnginePortalWindowConstants';
import { openNamedPortalTabAfterPromise } from './crossPortalAuth.utils';
import { getMsalInstance } from '../services/msalBootstrap.service';
import { getValidAccessToken } from './tokenCache.utils';
import { mergePortalAppearanceIntoUrl } from './portalAppearance.utils';

export { mergePortalAppearanceIntoUrl } from './portalAppearance.utils';

/**
 * Build Form Engine Portal base URL (no trailing slash).
 */
function getFormEngineBaseUrl(): string {
  return String(FORM_ENGINE_PORTAL_URL ?? '').replace(/\/$/, '');
}

/**
 * When VITE_FORM_ENGINE_PORTAL_URL points at Healthcare Portal (same origin),
 * redirects must target the real Form Engine Portal (default localhost:8080).
 */
function getEffectiveFormEngineBaseUrl(): string {
  const configured = getFormEngineBaseUrl();
  const fallback = 'http://localhost:8080';
  if (!configured) return fallback;
  if (typeof window === 'undefined') return configured;
  try {
    const configuredOrigin = new URL(configured).origin;
    if (configuredOrigin === window.location.origin) {
      return fallback;
    }
  } catch {
    return fallback;
  }
  return configured;
}

function isAbsoluteHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function resolveFormEnginePath(path?: string): string {
  if (!path) return '';
  if (path.startsWith('/') || path.startsWith('?')) return path;
  return `/${path}`;
}

/**
 * Get the Form Engine Portal URL with the current access token in the hash.
 * Use this when redirecting or opening FormEnginePortal so it receives the same token.
 *
 * @param path - Optional path (e.g. '/form/123' or '/examform?eid=456'), or an absolute http(s) URL
 *   (local-dev override). Leading slash optional for relative paths.
 * @returns URL with #access_token=<token>, or URL without token if not available (fallback).
 */
export async function getFormEnginePortalUrlWithAuth(path?: string): Promise<string> {
  const urlWithoutHash = mergePortalAppearanceIntoUrl(
    path && isAbsoluteHttpUrl(path)
      ? path
      : `${getEffectiveFormEngineBaseUrl()}${resolveFormEnginePath(path)}`
  );

  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    if (import.meta.env.DEV) {
      console.warn('[FormEnginePortal] MSAL instance not available, redirecting without token');
    }
    return urlWithoutHash;
  }

  const token = await getValidAccessToken(msalInstance);
  if (!token) {
    if (import.meta.env.DEV) {
      console.warn('[FormEnginePortal] No access token available, redirecting without token');
    }
    return urlWithoutHash;
  }

  return `${urlWithoutHash}#${SHARED_AUTH_ACCESS_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}

let formEnginePortalWindowRef: Window | null = null;

/**
 * Open Form Engine Portal in the named tab with the current access token.
 * Reuses the same browser tab when Form Engine is already open.
 *
 * Call synchronously from a click handler when possible: opens `about:blank` in the
 * named tab first, then navigates after the token URL resolves (popup-blocker safe).
 * Omits `noopener` so `window.opener` remains for storage-transfer postMessage.
 *
 * @param path - Optional path (e.g. 'form/123' or '/examform'), or an absolute http(s) URL.
 */
export function openFormEnginePortalWithAuth(path?: string): void {
  openNamedPortalTabAfterPromise(() => getFormEnginePortalUrlWithAuth(path), {
    windowName: FORM_ENGINE_PORTAL_WINDOW_NAME,
    getExistingWindow: () => formEnginePortalWindowRef,
    setExistingWindow: (win) => {
      formEnginePortalWindowRef = win;
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.warn('[FormEnginePortal] Failed to open named tab:', error);
      }
    },
  });
}

/**
 * Navigate to Form Engine Portal in the current window (token in hash).
 * Used when the user must stay in the same tab (e.g. Continue Part A).
 */
export function redirectToFormEnginePortalWithAuth(path?: string): void {
  void getFormEnginePortalUrlWithAuth(path).then((url) => {
    window.location.assign(url);
  });
}

/**
 * Get Form Engine Portal origin for postMessage targetOrigin (no trailing slash).
 */
function getFormEnginePortalOrigin(): string {
  const base = getEffectiveFormEngineBaseUrl();
  try {
    return new URL(base).origin;
  } catch {
    return base;
  }
}

/**
 * Copy all localStorage/sessionStorage into plain objects (same keys).
 */
function captureStorage(): { localStorage: Record<string, string>; sessionStorage: Record<string, string> } {
  const local: Record<string, string> = {};
  const session: Record<string, string> = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key != null) local[key] = window.localStorage.getItem(key) ?? '';
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[FormEnginePortal] Failed to read localStorage:', e);
  }
  try {
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key != null) session[key] = window.sessionStorage.getItem(key) ?? '';
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[FormEnginePortal] Failed to read sessionStorage:', e);
  }
  return { localStorage: local, sessionStorage: session };
}

/**
 * Register a message listener that sends all localStorage and sessionStorage to FormEnginePortal
 * when it requests them (opener → this window). Use inside useEffect and call the returned
 * unsubscribe on cleanup (same handler reference as removeEventListener requires).
 *
 * @returns Unsubscribe, or a no-op if window is unavailable (e.g. SSR).
 */
export function registerFormEngineStorageTransferListener(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const formEngineOrigin = getFormEnginePortalOrigin();
  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== formEngineOrigin) return;
    if (event.data?.type !== STORAGE_TRANSFER_REQUEST) return;
    const source = event.source;
    if (!source || typeof (source as Window).postMessage !== 'function') return;
    try {
      const { localStorage: localData, sessionStorage: sessionData } = captureStorage();
      // source.postMessage throws when COOP from login.live.com severs the window
      // reference after logoutRedirect. FEP already received the access token via
      // URL hash so auth still works; storage sync is best-effort.
      (source as Window).postMessage(
        {
          type: STORAGE_TRANSFER_RESPONSE,
          localStorage: localData,
          sessionStorage: sessionData,
        },
        formEngineOrigin
      );
      if (import.meta.env.DEV) {
        console.log(
          '[FormEnginePortal] Sent storage to FormEnginePortal:',
          Object.keys(localData).length,
          'localStorage,',
          Object.keys(sessionData).length,
          'sessionStorage'
        );
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[FormEnginePortal] Failed to send storage to FormEnginePortal:', e);
    }
  };
  window.addEventListener('message', onMessage);
  return () => {
    window.removeEventListener('message', onMessage);
  };
}
